import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { inr } from "../lib/format";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clear } = useCart();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const [amount, setAmount] = useState(null);
  const polls = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let timer;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        setAmount(res.data.amount);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (["expired", "failed"].includes(res.data.payment_status)) { setStatus("failed"); return; }
      } catch (e) { console.error("Payment status poll failed, retrying:", e); }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { setStatus("failed"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-6 py-28 text-center" data-testid="payment-success-page">
      {status === "checking" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Loader2 className="w-12 h-12 text-gold animate-spin mx-auto" />
          <h1 className="font-serif text-3xl text-gold-light mt-6">Confirming your payment…</h1>
          <p className="text-muted-foreground mt-2">This only takes a moment.</p>
        </motion.div>
      )}
      {status === "paid" && (
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} data-testid="payment-confirmed">
          <CheckCircle2 className="w-16 h-16 text-gold mx-auto" />
          <h1 className="font-serif text-4xl text-gold-light mt-6">Order confirmed</h1>
          <p className="text-muted-foreground mt-3">Thank you for shopping with Tashora. {amount ? `You paid ${inr(amount)}.` : ""} Your handwoven treasure is on its way.</p>
          <div className="flex gap-3 justify-center mt-8">
            <button onClick={() => navigate("/account")} data-testid="view-orders-button" className="bg-gold text-obsidian font-semibold px-6 py-3 rounded-full hover:bg-gold-light transition-colors">View my orders</button>
            <Link to="/collection" className="border border-gold/40 text-gold-muted px-6 py-3 rounded-full hover:bg-gold/10 transition-colors">Keep shopping</Link>
          </div>
        </motion.div>
      )}
      {status === "failed" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="payment-failed">
          <XCircle className="w-14 h-14 text-destructive mx-auto" />
          <h1 className="font-serif text-3xl text-gold-light mt-6">We couldn't confirm your payment</h1>
          <p className="text-muted-foreground mt-2">If you were charged, it will reflect in your orders shortly.</p>
          <Link to="/collection" className="mt-8 inline-block bg-gold text-obsidian font-semibold px-6 py-3 rounded-full">Back to collection</Link>
        </motion.div>
      )}
    </div>
  );
}
